/**
 * space/naming-ui.js — naming a fish you just won (GDD 9.3).
 *
 * "A named fish you won on a specific day becomes a memory, not just an
 * inventory item" is the whole point of GDD 9.5, so the prompt has to be easy
 * to answer and impossible to get stuck in. Every path out of it names the fish:
 * type one, take a suggestion, roll a random pun, or keep the default.
 *
 * Two presentations of one state machine, because a DOM overlay is invisible
 * in a headset and an on-screen keyboard is miserable on a desktop:
 *   - a DOM card with a real text field   → desktop
 *   - an in-world panel parented to the camera → VR (and desktop, as a preview)
 *
 * @module space/naming-ui
 */

import * as THREE from 'three';
import { FISH_NAME_SUGGESTIONS } from '../constants.js';
import { pick } from '../core/testmode.js';
import { createPanel } from '../ui/panels.js';
import { input } from '../core/input.js';
import { pointer } from './pointer.js';

const DOM_ID = 'cc-space-naming';

/**
 * The naming queue and its prompt.
 */
export class NamingUI {
  /**
   * @param {{ onName: (fishId: string, name: string) => void,
   *           defaultName: (fishId: string) => string }} hooks
   */
  constructor(hooks) {
    this.hooks = hooks;
    /** @type {string[]} Fish waiting to be named, oldest first. */
    this.queue = [];
    /** @type {string|null} */ this.activeId = null;
    /** @type {string[]} */ this.choices = [];

    this._panel = null;
    this._camera = null;
    this._dom = null;
    this._pointerWas = false;
    this._buttonIds = [];
  }

  /** @param {THREE.Camera} camera */
  init(camera) {
    this._camera = camera;
    return this;
  }

  // ── Queue ─────────────────────────────────────────────────────────────────

  /**
   * Remember that a fish needs a name. Called from the DIRECT_PRIZE handler,
   * so it must stay side-effect free — a round in progress must not be
   * interrupted by a modal.
   * @param {string} fishId
   */
  enqueue(fishId) {
    if (!this.queue.includes(fishId)) this.queue.push(fishId);
  }

  /** @returns {string[]} */
  pending() {
    return this.queue.slice();
  }

  /** @returns {boolean} */
  isOpen() {
    return this.activeId !== null;
  }

  /** @returns {string[]} The three suggestions currently offered. */
  suggestions() {
    return this.choices.slice();
  }

  // ── Prompt ────────────────────────────────────────────────────────────────

  /**
   * Open the prompt. With no argument it takes the head of the queue.
   * @param {string} [fishId]
   * @returns {boolean} False when there is nothing to name.
   */
  open(fishId = null) {
    const id = fishId || this.queue[0];
    if (!id) return false;
    this.close();
    this.activeId = id;
    this.choices = this._rollChoices();
    this._buildPanel();
    this._buildDom();
    return true;
  }

  /**
   * Name the active fish and move on to the next one in the queue.
   * @param {string} name
   * @returns {boolean}
   */
  submit(name) {
    const id = this.activeId;
    if (!id) return false;
    const clean = String(name || '').trim().slice(0, 24);
    if (clean) this.hooks.onName(id, clean);
    this._dequeue(id);
    this.close();
    // Straight on to the next fish — winning three at once should be three
    // quick decisions, not three trips back to the tank.
    if (this.queue.length) this.open();
    return true;
  }

  /** Take one of the offered puns at random. @returns {boolean} */
  random() {
    return this.submit(pick(this.choices.length ? this.choices : FISH_NAME_SUGGESTIONS));
  }

  /**
   * Accept the default "Fish #N". Dismissing the prompt does the same thing —
   * there is no way to leave a fish in limbo.
   * @returns {boolean}
   */
  keep() {
    const id = this.activeId;
    if (!id) return false;
    this.hooks.onName(id, this.hooks.defaultName(id));
    this._dequeue(id);
    this.close();
    if (this.queue.length) this.open();
    return true;
  }

  /** @returns {boolean} */
  dismiss() {
    return this.keep();
  }

  close() {
    this.activeId = null;
    for (const id of this._buttonIds) pointer.unregister(id);
    this._buttonIds.length = 0;
    if (this._panel) {
      this._panel.parent?.remove(this._panel);
      this._panel.dispose?.();
      this._panel = null;
      pointer.enabled = this._pointerWas;
    }
    this._dom?.remove();
    this._dom = null;
  }

  /** @private */
  _dequeue(id) {
    this.queue = this.queue.filter((q) => q !== id);
  }

  /** @private @returns {string[]} Three distinct puns. */
  _rollChoices() {
    const out = [];
    let guard = 0;
    while (out.length < 3 && guard++ < 40) {
      const name = pick(FISH_NAME_SUGGESTIONS);
      if (!out.includes(name)) out.push(name);
    }
    return out;
  }

  // ── In-world panel ────────────────────────────────────────────────────────

  /**
   * The in-world half of the prompt. VR only: on the desktop the DOM card is
   * the real UI, and drawing both put a metre-wide panel between the player and
   * the tank they had just won a fish for.
   * @private
   */
  _buildPanel() {
    if (!this._camera || !input.isVR) return;
    this._pointerWas = pointer.enabled;
    pointer.enabled = true;

    const holder = createPanel({
      title: 'NAME YOUR FISH', width: 0.78, height: 0.52,
      lines: ['Pick one, or keep it'],
    });
    holder.position.set(0, -0.02, -0.95);
    holder.renderOrder = 7000;
    this._camera.add(holder);
    this._panel = holder;

    const rows = [
      ...this.choices.map((c) => ({ id: `name:${c}`, label: c, bg: '#26304f' })),
      { id: '__random', label: 'RANDOM', bg: '#3a2b52' },
      { id: '__keep', label: 'KEEP DEFAULT', bg: '#232838' },
    ];
    rows.forEach((row, i) => {
      const btn = createPanel({
        width: 0.66, height: 0.062, lines: [row.label], bg: row.bg, radius: 12,
        accent: 'rgba(111,231,196,0.55)',
      });
      btn.position.set(0, 0.115 - i * 0.072, 0.004);
      holder.add(btn);

      const pid = `naming:${i}`;
      this._buttonIds.push(pid);
      pointer.register(btn, {
        id: pid,
        group: 'naming',
        label: row.label,
        onHover: (on) => btn.setStyle({ bg: on ? '#41528a' : row.bg }),
        onSelect: () => {
          if (row.id === '__random') this.random();
          else if (row.id === '__keep') this.keep();
          else this.submit(row.label);
        },
      });
    });
  }

  // ── DOM card ──────────────────────────────────────────────────────────────

  /** @private */
  _buildDom() {
    if (typeof document === 'undefined') return;
    document.getElementById(DOM_ID)?.remove();

    const card = document.createElement('div');
    card.id = DOM_ID;
    card.setAttribute('style', [
      'position:fixed', 'left:50%', 'top:14%', 'transform:translateX(-50%)',
      'z-index:60', 'min-width:320px', 'padding:18px 20px',
      'background:rgba(16,20,36,0.94)', 'border:2px solid rgba(255,209,102,0.75)',
      'border-radius:16px', 'color:#e8eefc',
      'font:600 15px/1.4 "Trebuchet MS",sans-serif', 'text-align:center',
      'box-shadow:0 12px 40px rgba(0,0,0,0.55)',
    ].join(';'));

    const title = document.createElement('div');
    title.textContent = 'Name your new fish';
    title.setAttribute('style', 'color:#ffd166;font-size:18px;margin-bottom:12px');
    card.appendChild(title);

    const field = document.createElement('input');
    field.id = `${DOM_ID}-input`;
    field.type = 'text';
    field.maxLength = 24;
    field.placeholder = this.hooks.defaultName(this.activeId);
    field.setAttribute('style', [
      'width:100%', 'box-sizing:border-box', 'padding:9px 11px', 'margin-bottom:10px',
      'background:#0d1120', 'color:#e8eefc', 'border:1px solid #3a4670',
      'border-radius:9px', 'font:600 15px "Trebuchet MS",sans-serif', 'text-align:center',
    ].join(';'));
    field.addEventListener('keydown', (e) => {
      e.stopPropagation();
      if (e.key === 'Enter') this.submit(field.value || field.placeholder);
      if (e.key === 'Escape') this.keep();
    });
    field.addEventListener('keyup', (e) => e.stopPropagation());
    card.appendChild(field);

    const row = document.createElement('div');
    row.setAttribute('style', 'display:flex;gap:6px;flex-wrap:wrap;justify-content:center');
    for (const choice of this.choices) {
      row.appendChild(this._domButton(choice, '#26304f', () => this.submit(choice)));
    }
    card.appendChild(row);

    const actions = document.createElement('div');
    actions.setAttribute('style', 'display:flex;gap:8px;justify-content:center;margin-top:12px');
    actions.appendChild(this._domButton('Random', '#3a2b52', () => this.random()));
    actions.appendChild(this._domButton('Name it', '#1d5c46', () => this.submit(field.value || field.placeholder)));
    actions.appendChild(this._domButton('Keep default', '#2a3145', () => this.keep()));
    card.appendChild(actions);

    document.body.appendChild(card);
    this._dom = card;
    field.focus();
  }

  /** @private */
  _domButton(label, bg, onClick) {
    const b = document.createElement('button');
    b.type = 'button';
    b.textContent = label;
    b.className = 'cc-space-name-btn';
    b.setAttribute('style', [
      `background:${bg}`, 'color:#e8eefc', 'border:1px solid rgba(255,255,255,0.16)',
      'border-radius:9px', 'padding:8px 12px', 'cursor:pointer',
      'font:600 13px "Trebuchet MS",sans-serif',
    ].join(';'));
    b.addEventListener('click', onClick);
    return b;
  }
}
